import { useState, useEffect } from 'react';
import { supabase } from './lib/supabase';
import './landing.css';
import { Nav } from './Nav';
import type { AppPage } from './LandingPage';

interface PricingPageProps {
  onNavigate: (page: AppPage) => void;
  onGetStarted: () => void;
  onLogin: () => void;
  isAuthenticated?: boolean;
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M2 7l3.5 3.5L12 3" />
    </svg>
  );
}

const PLANS = [
  {
    name: 'Starter',
    price: { monthly: 0, annual: 0 },
    desc: 'Everything you need to start closing smarter. No card required.',
    features: [
      '10 analyses per month',
      '3 AI responses per analysis',
      'Inline suggestions — up to 25/day',
      'Dashboard analytics',
      'All platforms supported',
      'Community support',
    ],
    cta: 'Get started free',
    highlight: false,
    teamPlan: false,
  },
  {
    name: 'Basic',
    price: { monthly: 29, annual: 23 },
    desc: 'For individual reps who need consistent context and volume.',
    features: [
      '150 analyses per month',
      '3 AI responses per analysis',
      'Inline suggestions — up to 75/day',
      'Unlimited auto-learning knowledge base',
      'Response refinement',
      'Dashboard analytics',
      'All platforms supported',
      'Standard email support',
    ],
    cta: 'Upgrade to Basic',
    highlight: false,
    teamPlan: false,
  },
  {
    name: 'Pro',
    price: { monthly: 79, annual: 63 },
    desc: 'For top closers who need the highest-quality intelligence.',
    features: [
      '500 analyses per month',
      '3 AI responses per analysis',
      'Inline suggestions — up to 200/day',
      'Unlimited auto-learning knowledge base',
      'Response refinement',
      'Dashboard analytics',
      'All platforms supported',
      'Priority email support',
    ],
    cta: 'Upgrade to Pro',
    highlight: true,
    teamPlan: false,
  },
  {
    name: 'Team',
    price: { monthly: 99, annual: 79 },
    desc: 'For sales teams who need shared context and visibility. (Per user)',
    features: [
      '5,000 analyses per month',
      'Everything in Pro',
      'Unlimited seats',
      'Dedicated onboarding call',
      'Team reporting (coming soon)',
      'Early access to custom playbooks',
    ],
    cta: 'Contact sales',
    highlight: false,
    teamPlan: true,
  },
];

const FAQ = [
  {
    q: 'Which platforms does Salifly support?',
    a: 'Salifly works anywhere you sell in a browser tab: Gmail, LinkedIn, Salesforce, HubSpot, Outreach, Gong, Outlook.com, and any other platform with a conversation thread. The extension detects the platform automatically. No configuration needed.',
  },
  {
    q: 'Is my conversation data stored permanently?',
    a: 'No. Your conversation is used only to generate responses and is not retained after the session ends. Only a lightweight interaction snapshot (deal stage, confidence level, and response metadata) is stored for your analytics, and it auto-deletes after 30 days. Personal information is automatically removed before any data leaves your device.',
  },
  {
    q: "How does Salifly detect which deal stage I'm in?",
    a: "Salifly reads the full conversation and page context, detecting signals like objection language, buying intent, timeline mentions, and stakeholder involvement, then classifies the deal as Discovery, Pitch, Stalled, Negotiation, or Closing. You can always override this before generating responses.",
  },
  {
    q: 'Can I customize the tone or style of the responses?',
    a: 'Yes. You can set a custom tone, specify message length, define a goal (re-engage / advance / close), and add custom instructions that persist across sessions. Pro and Team plans unlock the full customization panel.',
  },
  {
    q: 'Does Salifly integrate with my CRM?',
    a: "Salifly does not require CRM integration. It reads context directly from the conversation thread. The website surfaces suggested next steps you can log to your CRM manually. Native two-way CRM sync is on the roadmap for Team plans.",
  },
  {
    q: 'Can I cancel my subscription?',
    a: "Yes. You can cancel your subscription at any time from your account settings. Cancellation takes effect at the end of the current billing period, and you will not be charged again.",
  },
  {
    q: 'Can I switch plans later?',
    a: 'Yes. You can upgrade or downgrade at any time from your account settings. Upgrades are effective immediately and prorated. Downgrades take effect at the start of your next billing cycle.',
  },
];

export function PricingPage({ onNavigate, onGetStarted, onLogin, isAuthenticated }: PricingPageProps) {
  const [billing, setBilling] = useState<'monthly' | 'annual'>('monthly');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [activePlan, setActivePlan] = useState<'Starter' | 'Basic' | 'Pro' | null>(null);
  // Name of the plan currently mid-checkout-redirect; disables all CTAs.
  const [checkoutPlan, setCheckoutPlan] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState('');

  useEffect(() => {
    // Fetch subscription status
    const checkSub = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { data } = await supabase
          .from('profiles')
          .select('subscription_status, price_id')
          .eq('id', session.user.id)
          .single();
        if (data?.subscription_status === 'active' || data?.subscription_status === 'trialing') {
          const priceId = data.price_id;
          if (priceId && (priceId === 'price_1TaKQmPWi47nn0IbWT6zlFdI' || priceId === 'price_1TaKQrPWi47nn0Ibf8YHTzTJ')) {
            setActivePlan('Basic');
          } else {
            setActivePlan('Pro');
          }
        } else {
          setActivePlan('Starter');
        }
      } else {
        setActivePlan(null); // Not logged in
      }
    };
    checkSub();

    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => e.isIntersecting && e.target.classList.add('is-visible')),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handlePlanCta = async (plan: typeof PLANS[0]) => {
    if (plan.teamPlan) {
      window.location.href = 'mailto:sales@salifly.com';
      return;
    }

    if (plan.name === 'Pro' || plan.name === 'Basic') {
      // Signed-out visitors create an account first, then upgrade from here.
      if (activePlan === null) {
        onGetStarted();
        return;
      }
      setCheckoutError('');
      setCheckoutPlan(plan.name);
      try {
        const { data, error } = await supabase.functions.invoke('create-checkout-session', {
          body: { plan: plan.name, billing },
        });
        if (error || !data?.url) {
          throw new Error('Could not start checkout. Please try again.');
        }
        window.location.href = data.url;
      } catch (err) {
        setCheckoutError(err instanceof Error ? err.message : 'Could not start checkout. Please try again.');
        setCheckoutPlan(null);
      }
      return;
    }

    // Default (Starter plan)
    onGetStarted();
  };

  return (
    <div className="page-wrap">
      <Nav currentPage="pricing" onNavigate={onNavigate} onGetStarted={onGetStarted} onLogin={onLogin} isAuthenticated={isAuthenticated} />

      {/* Hero */}
      <div className="page-hero">
        <span className="page-hero-eyebrow">Simple, transparent pricing</span>
        <h1 className="page-hero-h1">
          Start free.<br /><em>Upgrade when ready.</em>
        </h1>
        <p className="page-hero-sub">
          No hidden fees. No contracts. Cancel or change plans at any time.
        </p>
      </div>

      {/* Pricing cards */}
      <div className="pp-pricing-section">
        {/* Billing toggle */}
        <div className="lp-billing-toggle reveal" style={{ marginBottom: 48 }}>
          <button
            className={`lp-billing-btn${billing === 'monthly' ? ' lp-billing-btn--active' : ''}`}
            onClick={() => setBilling('monthly')}
          >
            Monthly
          </button>
          <button
            className={`lp-billing-btn${billing === 'annual' ? ' lp-billing-btn--active' : ''}`}
            onClick={() => setBilling('annual')}
          >
            Annual
            <span className="lp-billing-save">Save 20%</span>
          </button>
        </div>

        {checkoutError && (
          <p style={{ font: '400 13px/1.4 var(--font-sans)', color: 'var(--danger)', textAlign: 'center', marginBottom: 24 }}>
            {checkoutError}
          </p>
        )}

        <div className="lp-pricing-grid">
          {PLANS.map((plan, i) => (
            <div
              key={plan.name}
              className={`lp-pricing-card reveal reveal-delay-${i + 1}${plan.highlight ? ' lp-pricing-card--highlight' : ''}`}
            >
              {plan.highlight && <div className="lp-pricing-badge">Most popular</div>}
              <div className="lp-pricing-header">
                <p className="lp-pricing-name">{plan.name}</p>
                <div className="lp-pricing-price-row">
                  {plan.teamPlan ? (
                    <span className="lp-pricing-price" style={{ fontSize: '2.5rem' }}>Custom</span>
                  ) : (
                    <>
                      <span className="lp-pricing-price">
                        ${billing === 'annual' ? plan.price.annual : plan.price.monthly}
                      </span>
                      {plan.price.monthly > 0 && (
                        <span className="lp-pricing-period">/ mo</span>
                      )}
                      {plan.price.monthly === 0 && (
                        <span className="lp-pricing-period">forever</span>
                      )}
                    </>
                  )}
                </div>
                {billing === 'annual' && plan.price.monthly > 0 && !plan.teamPlan && (
                  <p className="lp-pricing-annual-note">
                    Billed annually · ${plan.price.annual * 12}/yr
                  </p>
                )}
                <p className="lp-pricing-desc">{plan.desc}</p>
              </div>
              <button
                className={`lp-pricing-cta${plan.highlight ? ' btn btn-primary' : ' btn btn-sec'}`}
                onClick={() => handlePlanCta(plan)}
                disabled={
                  (activePlan === 'Pro' && (plan.name === 'Pro' || plan.name === 'Starter' || plan.name === 'Basic')) ||
                  (activePlan === 'Basic' && (plan.name === 'Basic' || plan.name === 'Starter')) ||
                  checkoutPlan !== null
                }
              >
                {(activePlan === 'Pro' && plan.name === 'Pro') || (activePlan === 'Basic' && plan.name === 'Basic')
                  ? 'Current Plan'
                  : (activePlan === 'Pro' && (plan.name === 'Starter' || plan.name === 'Basic')) || (activePlan === 'Basic' && plan.name === 'Starter')
                  ? 'Included'
                  : checkoutPlan === plan.name
                  ? 'Redirecting…'
                  : plan.cta}
              </button>
              <ul className="lp-pricing-features">
                {plan.features.map(f => (
                  <li key={f} className="lp-pricing-feature">
                    <CheckIcon />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Guarantee */}
        <div className="reveal" style={{ textAlign: 'center', marginTop: 48 }}>
          <p style={{ font: '400 14px/1 var(--font-sans)', color: 'var(--fg-4)' }}>
            Cancel or switch plans anytime · No credit card required for Starter
          </p>
        </div>
      </div>

      {/* FAQ */}
      <div style={{ background: 'rgba(0,0,0,0.2)', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <div className="lp-section lp-faq" style={{ maxWidth: 760, padding: '80px 48px' }}>
          <p className="eyebrow lp-section-eyebrow reveal">Questions & answers</p>
          <h2 className="lp-section-h2 reveal reveal-delay-1">Everything you need to know</h2>
          <div className="lp-faq-list reveal reveal-delay-2">
            {FAQ.map((item, i) => (
              <div key={i} className={`lp-faq-item${openFaq === i ? ' lp-faq-item--open' : ''}`}>
                <button
                  className="lp-faq-q"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                >
                  <span>{item.q}</span>
                  <svg className="lp-faq-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div className="lp-faq-a">
                  <p>{item.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="page-footer-cta">
        <h2>Start reaching your goals today</h2>
        <p>Free to start. No credit card required. Cancel anytime.</p>
        <div className="page-footer-cta-actions">
          <button className="page-btn-primary" onClick={onGetStarted}>Get started free →</button>
          <button className="page-btn-ghost" onClick={() => onNavigate('features')}>See all features</button>
        </div>
      </div>

      {/* Footer */}
      <footer className="page-footer">
        <div className="page-footer-inner">
          <div className="page-footer-row">
            <p className="page-footer-copy">© 2025 Salifly. All rights reserved.</p>
            <div className="page-footer-links">
              <a href="/privacy" className="page-footer-link">Privacy</a>
              <a href="/terms" className="page-footer-link">Terms</a>
              <a href="mailto:hello@salifly.com" className="page-footer-link">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
